import { createRoot } from "react-dom/client";
import { combineReducers } from '@reduxjs/toolkit';

import {
    cookieManager,
    locationService,
    publicContentViewEventService,
    queryStringManager,
    unauthenticatedUserSessionManager,
    userAccountInfoService,
    userAccountManager,
    userPasswordManager,
    userSessionManager,
} from 'shared/lib/manager';

import { Auth } from 'aws-amplify';
import { DjangoApiManager } from 'shared/api/DjangoApiManager';

import { RetailerOnrampFormActionInterfaces } from 'apps/PublicSite/RetailerOnrampForm/actions';
import { ActionInterfaces as UserSessionActionInterfaces } from 'apps/UserSessionApp/actions';
import { UserSessionReducers } from 'apps/UserSessionApp/reducers';

import { SignUpAndLoginButtonsActionInterfaces, SignUpAndLoginButtonsActions } from './SignUpAndLoginButtons/actions';

import { ErrorNotificationDialogReducers } from 'shared/apps/ErrorNotificationDialog/reducers/reducers';
import { EmbeddedSignUpFormReducers } from 'shared/components/SignUpOrSignIn/EmbeddedSignUpForm/reducers';
import { SignUpFormActionInterfaces } from 'shared/components/SignUpOrSignIn/SignUpForm/actions';
import { SignUpOptionsReducers } from 'shared/components/SignUpOrSignIn/SignUpOptions/reducers';
import { SignUpAndLoginButtonsReducers } from './SignUpAndLoginButtons/reducers';

import { BevSpotDispatch, Provider } from 'shared/components/Provider';

import { SlimCreateRetailerFormReducers } from 'shared/components/SignUpOrSignIn/SlimCreateRetailerForm/reducers';
import { ConnectedDownloadGate } from './DownloadGate/DownloadGate';
import { ConnectedGetStartedForm } from './GetStartedForm/GetStartedForm';
import { ConnectedGetStartedWithEmail } from './GetStartedWithEmail/GetStartedWithEmail';
import { ConnectedPublicSite } from './PublicSite';
import { ConnectedRetailerOnrampForm, RETAILER_REQUIRED_DOWNLOAD_GATE_MOUNT_ELEMENT_ID } from './RetailerOnrampForm/RetailerOnrampForm';
import { ConnectedUserSignUpForm } from './UserSignUpForm/UserSignUpForm';

type IServices = SignUpAndLoginButtonsActionInterfaces.IServices & SignUpFormActionInterfaces.IServices & RetailerOnrampFormActionInterfaces.IServices & UserSessionActionInterfaces.IServices;
const services : IServices = {
    publicContentViewEventService,
    unauthenticatedUserSessionManager,
    userSessionManager,
    userSessionTerminator: userSessionManager,
    userSessionCreator: userSessionManager,
    userSessionReader: userSessionManager,
    userAccountInfoReader: userAccountInfoService,
    userAccountManager,
    userPasswordManager,
    cookieReader: cookieManager,
    djangoApiManager: new DjangoApiManager(),
    locationService,
    queryStringReader: queryStringManager,
    cookieDeleter: cookieManager,
};

const publicSiteAppReducers = combineReducers({
    signUpAndLoginButtonsState: SignUpAndLoginButtonsReducers,
    signUpOptionsState: SignUpOptionsReducers,
    embeddedSignUpFormState : EmbeddedSignUpFormReducers,
    errorNotificationDialogState: ErrorNotificationDialogReducers,
    slimCreateRetailerFormState: SlimCreateRetailerFormReducers,
    userSession: UserSessionReducers,
});

const publicSiteStore = Provider.createStore(publicSiteAppReducers, services);
type AppState = ReturnType<typeof publicSiteStore["getState"]>;
const dispatch = publicSiteStore.dispatch as BevSpotDispatch<AppState, {services: IServices}>;
dispatch(SignUpAndLoginButtonsActions.onMount());
Auth.configure(window.AMPLIFY_CONFIG);

const publicSiteRoot = createRoot(document.getElementById('public-site-app')!);
publicSiteRoot.render(
    <Provider appReducers={ publicSiteAppReducers } services={ services } store={ publicSiteStore }>
        <ConnectedPublicSite />
    </Provider>
);

const downloadGateElement = document.getElementById('download-gate-mount');
if (downloadGateElement) {
    const downloadGateRoot = createRoot(downloadGateElement);
    downloadGateRoot.render(
        <Provider appReducers={ publicSiteAppReducers } services={ services } store={ publicSiteStore }>
            <ConnectedDownloadGate />
        </Provider>
    );
}

const retailerRequiredDownloadGateElement = document.getElementById(RETAILER_REQUIRED_DOWNLOAD_GATE_MOUNT_ELEMENT_ID);
if (retailerRequiredDownloadGateElement) {
    const retailerRequiredDownloadGateRoot = createRoot(retailerRequiredDownloadGateElement);
    retailerRequiredDownloadGateRoot.render(
        <Provider appReducers={ publicSiteAppReducers } services={ services } store={ publicSiteStore }>
            <ConnectedRetailerOnrampForm />
        </Provider>
    );
}

const userSignUpElement = document.getElementById('user-sign-up-form-mount');
if (userSignUpElement) {
    const userSignUpRoot = createRoot(userSignUpElement);
    userSignUpRoot.render(
        <Provider appReducers={ publicSiteAppReducers } services={ services } store={ publicSiteStore }>
            <ConnectedUserSignUpForm />
        </Provider>
    );
}

const createRetailerFormElement = document.getElementById('create-retailer-form-mount');
if (createRetailerFormElement) {
    const createRetailerRoot = createRoot(createRetailerFormElement);
    createRetailerRoot.render(
        <Provider appReducers={ publicSiteAppReducers } services={ services } store={ publicSiteStore }>
            <ConnectedGetStartedForm />
        </Provider>
    );
}

const getStartedWithEmailElement = document.getElementById('get-started');
if (getStartedWithEmailElement) {
    const getStartedRoot = createRoot(getStartedWithEmailElement);
    getStartedRoot.render(
        <Provider appReducers={ publicSiteAppReducers } services={ services } store={ publicSiteStore }>
            <ConnectedGetStartedWithEmail/>,
        </Provider>
    );
}
